/*
 * <BeTable> -- React wrapper for the .be-table CSS component: the BeOS R5
 * Tracker list view (multi-column file list / query results).
 *
 * Reconstructed from the decompiled R5 Tracker (BTitleView/BColumnTitle for the
 * header, BPoseView/BPose/BTextWidget for the rows) -- see components/table.css
 * and Notes/06. Header cell and bevel are byte-sourced from the binary, not the
 * Dano controls kit. Selection is grey (152,152,152) with BLACK text
 * (shift_color(216, 1.295), byte-verified in BListView.Draw.match.cpp) -- this
 * header used to say #6464d2 + white, which the CSS token block had already
 * superseded as "not reproducible in the R5 source".
 *
 * Rendered as a real <table> (semantic + accessible). Sortable headers are
 * <button>s; selectable rows are keyboard-operable (Enter/Space).
 *
 *   <BeTable
 *     columns={[
 *       { id: "name", title: "Name", width: 180 },
 *       { id: "size", title: "Size", width: 70, align: "right", sortable: true },
 *     ]}
 *     rows={[{ id: "f1", icon: folderPng, cells: { name: "fonts", size: "-" } }]}
 *     selectedIds={["f1"]}
 *     onSelectItem={(id) => ...}
 *     sort={{ columnId: "name", ascending: true }}
 *     onSort={(id) => ...}
 *   />
 */

import type { CSSProperties, KeyboardEvent, ReactNode } from "react";
import { type BeProps, beComponent } from "./create.tsx";
import { type BeItem, beIconSrc } from "./item.ts";
import { useBeListKeyboard } from "./list-keyboard.ts";
import { pixelText } from "./pixel-text.tsx";

export type BeTableAlign = "left" | "right" | "center";

export type BeTableColumn = {
	/** Stable key; also the key into each row's `cells`. */
	id: string;
	/** Header label. */
	title: ReactNode;
	/** Column width in R5 pixels (scaled by --be-px). Ignored when `fill` is set. */
	width: number;
	/**
	 * Let THIS column absorb the table's slack instead of the trailing filler.
	 *
	 * `table-layout: fixed` hands every <col> its stated width and dumps the
	 * remainder into the one auto-width column. By default that is the filler, so
	 * a text column stays clamped at its `width` while dead space sits beside it
	 * (Activity Log: Detail pinned to 400 next to a 1019px filler). Marking the
	 * text column `fill` makes it the auto one instead, so long values get the
	 * room rather than being truncated.
	 *
	 * Only one column should carry this -- fixed layout splits the remainder
	 * EQUALLY between auto columns, so two fill columns each get half. For the
	 * same reason the trailing filler is suppressed when any column fills; it
	 * would otherwise take half the slack straight back.
	 */
	fill?: boolean;
	/** Field alignment. Defaults to "left" (BColumnTitle fAlignment). */
	align?: BeTableAlign;
	/** Clicking the header calls onSort. */
	sortable?: boolean;
};

export type BeTableRow = BeItem & {
	/** Cell content keyed by column id. A row's label is `cells[columns[0].id]`;
	 *  `BeItem` carries no `name` because that would be a second spelling of it. */
	cells: Record<string, ReactNode>;
	/** Tree mode: outline depth (0 = root). Indents the first column by level*10px. */
	level?: number;
	/** Tree mode: this row is a super-item (has children) -> draws a latch. */
	expandable?: boolean;
	/** Tree mode: latch state (controlled). Only meaningful when expandable. */
	expanded?: boolean;
};

export type BeTableSort = { columnId: string; ascending: boolean };

export type BeTableProps = BeProps<
	"table",
	{
		columns: BeTableColumn[];
		rows: BeTableRow[];
		/** Ids of selected rows (controlled). Selected rows fill grey 152 + black text
		 *  (--be-table-selection / --be-table-selection-text). */
		selectedIds?: string[];
		/** Called when an item is clicked/activated; makes items interactive. */
		onSelectItem?: (id: string, item: BeTableRow) => void;
		/** Called when an item is double-clicked. */
		onDoubleClickItem?: (id: string, item: BeTableRow) => void;
		/** Active sort column + direction; renders that header depressed. */
		sort?: BeTableSort;
		/** Called with a column id when a sortable header is clicked. */
		onSort?: (columnId: string) => void;
		/** Tree mode: called when an expandable row's latch is clicked. Pass only the
		 *  currently-visible rows (collapsed subtrees omitted), like a controlled list. */
		onToggleExpand?: (id: string, row: BeTableRow) => void;
		/** Hide the column-title bar. Defaults to shown. */
		showHeader?: boolean;
		/**
		 * Render a trailing "empty column" that fills space past the last real column
		 * (Tracker's stretch look). OFF by default -- only turn it on where the table
		 * is meant to fill a wider container (e.g. inside <BeScrollView>). With manual
		 * placement/sizing it is unneeded and would show as dead trailing space.
		 */
		fillTrailing?: boolean;
		/** Opt-in alternating-row shading (non-R5). */
		striped?: boolean;
		/** Opt-in visible tree connector guides (non-R5; tree mode only). */
		treeLines?: boolean;
		/** Tracker's form: the name's TEXT inverts, and the icon tints with it. */
		nameSelection?: boolean;
		/** Opt-in R5 first-column metrics: the header's edge line falls through the
		 *  icon rather than beside it. Costs 16px of extra lead. Needs icons. */
		splitHeader?: boolean;
	}
>;

function titleAlignClass(align?: BeTableAlign): string | null {
	if (align === "right") return "be-table__title--right";
	if (align === "center") return "be-table__title--center";
	return null;
}

function cellAlignClass(align?: BeTableAlign): string | null {
	if (align === "right") return "be-table__cell--right";
	if (align === "center") return "be-table__cell--center";
	return null;
}

const iconSrc = (row: BeTableRow): string | undefined => beIconSrc(row.icon, "mini");

function labelCls(row: BeTableRow): string {
	const cls = "be-table__cell-label";
	return row.decoration === "underline" ? `${cls} ${cls}--underline` : cls;
}

function colWidth(col: BeTableColumn): CSSProperties {
	// auto, so fixed layout gives it the remainder
	if (col.fill) return {};
	return { width: `calc(${col.width} * var(--be-px))` };
}

type GuideKind = "through" | "tee" | "end" | "none";

// Does a row at level `target` appear later, before we pop above `target`?
function continuesAt(rows: BeTableRow[], from: number, target: number): boolean {
	for (let j = from + 1; j < rows.length; j++) {
		const lj = rows[j]?.level ?? 0;
		if (lj < target) return false;
		if (lj === target) return true;
	}
	return false;
}

// Tree connector segments for one row: one cell per indent level. The rightmost
// cell is the elbow to this node (tee = has a sibling below, end = last child);
// the cells left of it draw a vertical only where that ancestor continues below.
function rowGuides(rows: BeTableRow[], i: number): GuideKind[] {
	const level = rows[i]?.level ?? 0;
	const out: GuideKind[] = [];
	for (let c = 0; c < level; c++) {
		const cont = continuesAt(rows, i, c + 1);
		out.push(c === level - 1 ? (cont ? "tee" : "end") : cont ? "through" : "none");
	}
	return out;
}

export const BeTable = beComponent<BeTableProps, HTMLTableElement>("BeTable", (props, be) => {
	const {
		columns,
		rows,
		selectedIds,
		onSelectItem,
		onDoubleClickItem,
		sort,
		onSort,
		onToggleExpand,
		showHeader = true,
		fillTrailing = false,
		striped,
		treeLines,
		splitHeader,
		nameSelection,
		scale,
		className,
		style,
		...rest
	} = props;

	const isTree = rows.some((r) => r.level || r.expandable);
	// resolved, not `r.icon` -- a BeIcon carrying neither size is truthy but draws nothing
	const hasIcons = rows.some((r) => iconSrc(r));
	// A fill column IS the stretch, so the trailing filler would only take half
	// the slack back off it (fixed layout splits the remainder between autos).
	const hasFill = columns.some((c) => c.fill);
	const showFiller = fillTrailing && !hasFill;
	// the lead is a real cell, so it owns its own press state; the name cell spans both
	const isSplit = Boolean(splitHeader) && hasIcons;

	const root = be(
		"be-table",
		isTree && "be-table--tree",
		hasIcons && "be-table--icons",
		(fillTrailing || hasFill) && "be-table--fill",
		striped && "be-table--striped",
		treeLines && isTree && "be-table--tree-lines",
		isSplit && "be-table--split-header",
		nameSelection && "be-table--name-selection",
	);

	const selected = new Set(selectedIds ?? []);
	const selectable = onSelectItem !== undefined;

	function firstCellText(row: BeTableRow): string | null {
		const col0 = columns[0];
		if (!col0) return null;
		const c = row.cells[col0.id];
		return typeof c === "string" ? c : null;
	}

	const nav = useBeListKeyboard(rows, selected, { layout: "stack", textOf: firstCellText, onMove: onSelectItem, ref: root.ref });

	function handleRowKeyDown(e: KeyboardEvent<HTMLTableRowElement>, index: number) {
		const row = rows[index];
		if (!row) return;
		switch (e.key) {
			case "Enter":
			case " ":
				// arrowing already selects, so the key is free to be the ACTIVATION
				e.preventDefault();
				(onDoubleClickItem ?? onSelectItem)?.(row.id, row);
				return;
			// #319: tree keys, which a `stack` delegate leaves to us. Inert on a flat list.
			case "ArrowRight": {
				if (row.expandable && !row.expanded) {
					e.preventDefault();
					onToggleExpand?.(row.id, row);
				} else if (row.expandable && row.expanded) {
					const child = rows[index + 1];
					if (child && (child.level ?? 0) > (row.level ?? 0)) {
						e.preventDefault();
						nav.moveTo(child.id);
					}
				}
				return;
			}
			case "ArrowLeft": {
				if (row.expandable && row.expanded) {
					e.preventDefault();
					onToggleExpand?.(row.id, row);
				} else {
					const level = row.level ?? 0;
					for (let j = index - 1; j >= 0; j--) {
						const r = rows[j];
						if (r && (r.level ?? 0) < level) {
							e.preventDefault();
							nav.moveTo(r.id);
							break;
						}
					}
				}
				return;
			}
		}

		nav.handleKey(e, row.id);
	}

	// grid/treegrid promise a keyboard model, so the role is gated on `selectable` --
	// the same flag that wires tabIndex and onKeyDown. A read-only tree stays a plain
	// table; aria-level/-expanded are row-supported there anyway.
	const role = selectable ? (isTree ? "treegrid" : "grid") : undefined;

	return (
		<table role={role} {...rest} {...root} ref={nav.setContainer}>
			<colgroup>
				{isSplit && <col className="be-table__col--lead" />}
				{columns.map((col) => (
					<col key={col.id} style={colWidth(col)} />
				))}
				{showFiller && <col />}
			</colgroup>

			{showHeader && (
				<thead className="be-table__head">
					<tr>
						{isSplit && <th className="be-table__title be-table__title--lead" />}
						{columns.map((col) => {
							const isSorted = sort?.columnId === col.id;
							const cls = ["be-table__title", "be-text--mini", col === columns[0] && "be-table__title--name", titleAlignClass(col.align)]
								.filter(Boolean)
								.join(" ");
							return (
								<th
									key={col.id}
									className={cls}
									scope="col"
									aria-sort={isSorted ? (sort?.ascending ? "ascending" : "descending") : undefined}
								>
									{col.sortable && onSort ? (
										<button type="button" className="be-table__title-button" onClick={() => onSort(col.id)}>
											{pixelText(col.title)}
										</button>
									) : (
										pixelText(col.title)
									)}
									{isSorted && (
										<span className={`be-table__sort-arrow be-table__sort-arrow--${sort?.ascending ? "asc" : "desc"}`} aria-hidden="true" />
									)}
								</th>
							);
						})}
						{showFiller && <th className="be-table__title be-table__title--filler" />}
					</tr>
				</thead>
			)}

			<tbody className="be-table__body">
				{rows.map((row, index) => {
					const isSel = selected.has(row.id);
					const rowCls = ["be-table__row", selectable ? "be-table__row--selectable" : null].filter(Boolean).join(" ");
					return (
						<tr
							key={row.id}
							data-key={row.id}
							className={rowCls}
							aria-level={isTree ? (row.level ?? 0) + 1 : undefined}
							aria-expanded={isTree && row.expandable ? (row.expanded ?? false) : undefined}
							aria-selected={selectable && selectedIds ? isSel : undefined}
							tabIndex={selectable ? (row.id === nav.rovingId ? 0 : -1) : undefined}
							onClick={onSelectItem ? () => onSelectItem(row.id, row) : undefined}
							onDoubleClick={onDoubleClickItem ? () => onDoubleClickItem(row.id, row) : undefined}
							onFocus={selectable ? () => nav.setActiveId(row.id) : undefined}
							onKeyDown={selectable ? (e) => handleRowKeyDown(e, index) : undefined}
						>
							{columns.map((col, i) => {
								const content = row.cells[col.id];
								if (i === 0) {
									return (
										<td key={col.id} className="be-table__cell" colSpan={isSplit ? 2 : undefined}>
											<div className="be-table__cell-inner">
												{isTree &&
													(treeLines ? (
														rowGuides(rows, index).map((g, d) => (
															// biome-ignore lint/suspicious/noArrayIndexKey: guides are positional by depth
															<span key={d} className={`be-table__guide be-table__guide--${g}`} />
														))
													) : (
														<span className="be-table__indent" style={{ "--be-tree-level": row.level ?? 0 }} />
													))}
												{isTree &&
													(row.expandable ? (
														<button
															type="button"
															className="be-table__latch"
															aria-expanded={row.expanded ?? false}
															aria-label={row.expanded ? "Collapse" : "Expand"}
															onClick={(e) => {
																e.stopPropagation();
																onToggleExpand?.(row.id, row);
															}}
														/>
													) : (
														<span className="be-table__latch be-table__latch--spacer" />
													))}
												{iconSrc(row) ? (
													<img className="be-table__icon" src={iconSrc(row)} alt="" />
												) : hasIcons ? (
													<span className="be-table__icon be-table__icon--empty" />
												) : null}
												<span className={labelCls(row)}>{pixelText(content)}</span>
											</div>
										</td>
									);
								}
								const cls = ["be-table__cell", cellAlignClass(col.align)].filter(Boolean).join(" ");
								return (
									<td key={col.id} className={cls}>
										{pixelText(content)}
									</td>
								);
							})}
							{showFiller && <td className="be-table__cell be-table__cell--filler" />}
						</tr>
					);
				})}
			</tbody>
		</table>
	);
});
